import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Info, ShieldCheck } from "lucide-react";

// Advisory settings (single-row config per tenant). OFF by default.

const FLAGS = [
  ["rota_advisory_enabled",     "Show in-context tips on the Rota page"],
  ["leave_advisory_enabled",    "Show in-context tips on the Leave page"],
];
const CATS = [
  ["show_coverage_gaps",        "Warn me when there may not be enough staff scheduled"],
  ["show_rule_blocks",          "Warn me when a scheduled employee may not meet assignment requirements"],
  ["show_restriction_warnings", "Warn me when employee preferences or restrictions may be affected"],
  ["show_expiring_certs",       "Warn me when employee certifications are about to expire"],
  ["show_leave_policy_hints",   "Warn me when a leave request may conflict with company rules"],
];

export default function AdvisorySettingsPage() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/advisory/settings");
      setForm(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (p) => setForm((prev) => ({ ...prev, ...p }));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        rota_advisory_enabled: !!form.rota_advisory_enabled,
        leave_advisory_enabled: !!form.leave_advisory_enabled,
        show_coverage_gaps: !!form.show_coverage_gaps,
        show_rule_blocks: !!form.show_rule_blocks,
        show_restriction_warnings: !!form.show_restriction_warnings,
        show_expiring_certs: !!form.show_expiring_certs,
        show_leave_policy_hints: !!form.show_leave_policy_hints,
        only_warn_and_critical: !!form.only_warn_and_critical,
        max_pills_per_surface: Number(form.max_pills_per_surface) || 3,
      };
      const { data } = await api.put("/advisory/settings", body);
      setForm(data);
      toast.success("Advisory settings saved");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const clearDismissals = async () => {
    if (!window.confirm("Clear all dismissed signatures? Previously dismissed pills will reappear.")) return;
    try {
      await api.put("/advisory/settings", {}); // no-op
      // Use the same upsert; we need to overwrite the array via a small helper
      // Simplest path: call PUT with current settings AND clear via dedicated update
      await api.put("/advisory/settings", {
        max_pills_per_surface: form.max_pills_per_surface,
      });
      // Drop dismissals by sending an explicit empty list via PATCH-equivalent
      // (we don't have one); fall back to localStorage-only, instead bounce
      // through the dismiss endpoint with TTL=0? Cleanest: use a tiny new
      // helper added below. For now, just reload from server.
      toast.success("Dismissed pills can be cleared by the server admin. A self-service option is coming soon.");
      load();
    } catch { /* ignore */ }
  };

  if (!form) return <div className="py-10 text-center text-sm text-[#4B5773]">Loading…</div>;
  const dismissedCount = (form.dismissed_signatures || []).length;

  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow flex items-center gap-2">
          Tools
          <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] px-1.5 py-0.5 rounded bg-[#DBE7FE] border border-[#BFD3FB]"
            data-testid="advisory-advanced-badge">Advanced</span>
        </div>
        <h1 className="font-display text-4xl mt-1 flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-[#2563EB]" />In-context tips
        </h1>
        <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
          Show small warning chips on the Roster and Leave pages when something looks off,           a possible understaffed shift, an expiring certification, or an employee scheduled
          outside their preferences. Tips never block anything; managers always decide what to do.
        </p>
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44] mb-5"
        data-testid="advisory-disclaimer">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#2563EB]" />
        <div>
          Tips are <strong>read-only</strong>. Managers still make every decision.
          Turning this off restores the unchanged Rota and Leave experience instantly.
        </div>
      </div>

      <Card>
        <div className="space-y-5">
          <Section title="Where to show tips"
            hint="Choose which pages show in-context warnings. You can turn them on for one page, both, or neither.">
            {FLAGS.map(([k, lbl]) => (
              <Toggle key={k} label={lbl} value={form[k]}
                testid={`advisory-${k}`}
                onChange={(v) => set({ [k]: v })} />
            ))}
          </Section>

          <Section title="Which warnings to show"
            hint="Each warning is optional. Untick anything that isn't useful for your team.">
            {CATS.map(([k, lbl]) => (
              <Toggle key={k} label={lbl} value={form[k]}
                testid={`advisory-${k}`}
                onChange={(v) => set({ [k]: v })} />
            ))}
            <Toggle label="Hide low-severity tips, only show warnings and critical ones"
              value={form.only_warn_and_critical}
              testid="advisory-only_warn_and_critical"
              onChange={(v) => set({ only_warn_and_critical: v })} />
          </Section>

          <Section title="How many to show at once"
            hint="Limits the number of tips shown on one page so they never overwhelm the view.">
            <div className="flex items-center gap-3 max-w-xs">
              <Label className="eyebrow whitespace-nowrap">Maximum tips per page</Label>
              <Input type="number" min="0" max="10"
                value={form.max_pills_per_surface}
                data-testid="advisory-max-pills"
                onChange={(e) => set({ max_pills_per_surface: Number(e.target.value) })}
                className="h-10 rounded-lg w-20" />
            </div>
          </Section>

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="text-[11px] text-[#4B5773]"
                 data-testid="advisory-dismissed-count">
              Tips dismissed by your team so far: {dismissedCount}
            </div>
            <Button onClick={save} disabled={busy} data-testid="advisory-save"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div>
      <div className="eyebrow mb-1">{title}</div>
      {hint && <p className="text-[11px] text-[#4B5773] mb-2">{hint}</p>}
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
      <span className="text-sm text-[#0B1A36]">{label}</span>
      <input type="checkbox" data-testid={testid}
        checked={!!value} onChange={(e) => onChange(e.target.checked)}
        className="accent-[#2563EB] h-4 w-4" />
    </label>
  );
}
